import React, { JSX, useCallback } from "react";
import { Card } from "components/card/card";
import { Heading, HeadingPriority, HeadingSize } from "components/typography/heading/heading";
import { InputTypes } from "components/form/enumerations/input-types";
import { EventActiveRecord } from "models/active-records/events/event-active-record";
import { t } from "utilities/localization/t";
import { ToggleLabel } from "components/toggle/toggle-label/toggle-label";
import { FormTextArea } from "components/form/form-textarea/form-textarea";
import { FormTextInput } from "components/form/form-input/form-text-input";
import { FormCalendarDatePicker } from "components/form/form-calendar-date-picker/form-calendar-date-picker";
import "./event-invoice-form.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface EventInvoiceFormProps {
    event: EventActiveRecord;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME: string = "event-invoice-form";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const EventInvoiceForm: React.FC<EventInvoiceFormProps> = ({ event }): JSX.Element => {
    const handleBlur = useCallback((): void => {
        if (event.isDraft()) {
            event.patchEventInvoice(
                event.hasBeenInvoiced,
                event.invoiceNotes,
                event.invoiceAmount,
                event.invoiceDate
            );
        }
    }, [event]);

    const handleToggleChange = (checked: boolean): void => {
        if (checked && event.invoiceDate == null) {
            event.updateActiveRecord({
                invoiceDate: new Date(),
            });
        }
        if (event.isDraft()) {
            event.patchEventInvoice(
                checked,
                event.invoiceNotes,
                event.invoiceAmount,
                event.invoiceDate
            );
        } else {
            event.updateActiveRecord({
                hasBeenInvoiced: checked,
            });
        }
    };

    const handleNotesChange = (value: string): void => {
        event.updateActiveRecord({
            invoiceNotes: value,
        });
    };

    const handleInvoiceAmountChange = (value: string): void => {
        event.updateActiveRecord({
            invoiceAmount: +parseFloat(value).toFixed(2),
        });
    };

    const handleInvoiceDateChange = (date?: Date): void => {
        if (date == null) {
            return;
        }
        event.updateActiveRecord({
            invoiceDate: date,
        });
    };

    return (
        <>
            <Card>
                <div className={`${CSS_CLASS_NAME}__header`}>
                    <Heading priority={HeadingPriority.H2} size={HeadingSize.XSmall}>
                        {t("eventInvoicing")}
                    </Heading>
                    <ToggleLabel
                        checked={event.hasBeenInvoiced ?? false}
                        id="has-been-invoiced"
                        label={t("hasbeeninvoiced")}
                        onToggle={(checked) => handleToggleChange(checked)}
                        disabled={event.isDraft() ? true : false}
                    />
                </div>
                <div className={`${CSS_CLASS_NAME}__grid`}>
                    <FormTextArea
                        ariaLabelledBy={t("notes")}
                        autoFocus={false}
                        formFieldName="InvoiceNotes"
                        id="InvoiceNotes"
                        rows={8}
                        label={t("notes")}
                        onBlur={handleBlur}
                        onChange={(e) => handleNotesChange(e.target.value)}
                        placeholder={t("enterNotes")}
                        required={false}
                        type={InputTypes.Text}
                        value={event.invoiceNotes}
                        disabled={!event.hasBeenInvoiced}
                        maxLength={200}
                    />
                    <div>
                        <FormTextInput
                            disabled={true}
                            readOnly={true}
                            ariaLabelledBy={"totalLearners"}
                            formFieldName={"totalLearners"}
                            id={"totalLearners"}
                            label={"Total Learners"}
                            placeholder={"Total Learners"}
                            value={event.totalLearners}
                            onChange={() => {}}
                        />
                        <FormTextInput
                            disabled={true}
                            readOnly={true}
                            ariaLabelledBy={"Price Per Learner"}
                            formFieldName={"Price per Learner"}
                            id={"pricePerLearner"}
                            label={"Price Per Learner"}
                            placeholder={"Price Per Learner"}
                            value={`$${event.product?.pricePerLearner?.toFixed(2) ?? "0.00"}`}
                            onChange={() => {}}
                        />

                        <FormTextInput
                            disabled={true}
                            readOnly={true}
                            ariaLabelledBy={"Suggested Invoice Amount"}
                            formFieldName={"Suggested Invoice Amount"}
                            id={"suggestedInvoiceAmount"}
                            label={"Suggested Invoice Amount"}
                            placeholder={"Suggested Invoice Amount"}
                            value={
                                "$" +
                                (
                                    event.totalLearners * (event.product?.pricePerLearner ?? 0)
                                ).toFixed(2)
                            }
                            onChange={() => {}}
                        />
                    </div>
                </div>
                <div className={`${CSS_CLASS_NAME}__grid2`}>
                    <div>
                        <FormTextInput
                            type={InputTypes.Number}
                            ariaLabelledBy={"invoiceAmount"}
                            disabled={!event.hasBeenInvoiced}
                            formFieldName={"invoiceAmount"}
                            id={"invoiceAmount"}
                            label={"Invoice Amount"}
                            minValue={0}
                            placeholder={"Invoice Amount"}
                            required
                            value={event.invoiceAmount}
                            onChange={(e) => handleInvoiceAmountChange(e.target.value)}
                        />
                    </div>
                    <div>
                        <FormCalendarDatePicker
                            cssClassName={`${CSS_CLASS_NAME}__form-calendar-date-picker`}
                            required
                            formFieldName={"invoiceDate"}
                            label={"Invoice Date"}
                            onChange={handleInvoiceDateChange}
                            placeholder={"emptyDate"}
                            readOnly={!event.hasBeenInvoiced}
                            selectedDate={event.invoiceDate}
                        />
                    </div>
                </div>
            </Card>
        </>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { EventInvoiceForm };

// #endregion Exports
